import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/di/container';
import { QUERY_KEYS } from '@shared/constants';
import type {
  CreateMockServerPayload,
  MockRoutePayload,
  UpdateMockServerPayload,
} from '@core/repositories/MockServerRepository';

export const useMockServers = (workspaceId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.mockServers(workspaceId ?? ''),
    queryFn: () => container.mockServers.list(workspaceId as string),
    enabled: Boolean(workspaceId),
  });
};

export const useCreateMockServer = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateMockServerPayload) =>
      container.mockServers.create(workspaceId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.mockServers(workspaceId) }),
  });
};

export const useUpdateMockServer = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMockServerPayload }) =>
      container.mockServers.update(id, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.mockServers(workspaceId) }),
  });
};

export const useDeleteMockServer = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => container.mockServers.remove(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.mockServers(workspaceId) }),
  });
};

export const useCreateMockRoute = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mockServerId, payload }: { mockServerId: string; payload: MockRoutePayload }) =>
      container.mockServers.createRoute(mockServerId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.mockServers(workspaceId) }),
  });
};

export const useUpdateMockRoute = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ routeId, payload }: { routeId: string; payload: MockRoutePayload }) =>
      container.mockServers.updateRoute(routeId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.mockServers(workspaceId) }),
  });
};

export const useDeleteMockRoute = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (routeId: string) => container.mockServers.removeRoute(routeId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.mockServers(workspaceId) }),
  });
};
